import {
  Box,
  Flex,
  FormControl,
  FormLabel,
  GridItem,
  IconButton,
  Img,
  Input,
  InputGroup,
  InputRightAddon,
  Text,
  Tooltip,
  useToast,
} from '@chakra-ui/react';
import { Composition } from '@texas/api/endpoints/compositionApi';
import { techniquesApi } from '@texas/api/endpoints/metadata/techniquesApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { AutoUpdateWrapper } from '@texas/components/shared/autoUpdate/useAutoUpdateForm';
import { ErrorLabel } from '@texas/components/shared/ErrorLabel';
import { TexasSelect } from '@texas/components/shared/form/TexasSelect';
import { Icons } from '@texas/components/shared/Icons';
import {
  MetadataList,
  MetadataListRow,
} from '@texas/components/shared/MetadataList';
import { useActiveContext } from '@texas/hooks/useActiveContext';
import { fadeInScaleAnimation } from '@texas/resources/animations/animations';
import { onlyUnique, sum } from '@texas/utils/helpers/commonHelpers';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { variantsApi } from '@texas/api/endpoints/variantsApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { request } from '@texas/utils/helpers/httpHelpers';
import { Photo } from '../../../variant/general/files/Photo';
import { setValueAsDecimalExtender } from '../../../variant/sales/shared';
import { GarpFormContext } from '../SetGarpDataModal';
import {
  Colors,
  Dimensions,
  Materials,
  Techniques,
  Weight,
} from '../shared/Components';
import { GarpTextField } from '../shared/GarpTextField';
import {
  extractCombinedMaterial,
  extractDimensionSize,
} from '../shared/helper';
import { clientEndpoints } from '@texas/clientEndpoints';
import { ExpandImage } from '@texas/components/shared/image/ExpandImage';
import { variantPhotoEvents } from '../../../variant/general/files/variantPhotoEvents';

const flexGap = 2;

export function GarpPhoto({
  variantId,
  folderId,
  paFileIdentifier,
  paFileId,
}: {
  variantId: number;
  folderId: number;
  paFileIdentifier?: string | null;
  paFileId?: number | null;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const { request: updateRequest, loading } = useApiRequest(
    variantsApi.updatePhoto,
  );
  const [variantPhotoId, setVariantPhotoId] = useState<number | null>(null);

  const copyPaPhoto = useCallback(() => {
    if (!paFileId || !paFileIdentifier) return;
    request(
      updateRequest,
      [variantId, paFileId],
      (_) => {
        toast({
          title: t('fileBrowser.updatedFile'),
          status: 'success',
          isClosable: true,
        });
        variantPhotoEvents.photoUpdated.dispatch(variantId);
      },
      (_) => {
        toast({
          title: t('fileBrowser.updateFileFailed'),
          status: 'error',
          isClosable: true,
        });
      },
    );
  }, [paFileId, paFileIdentifier, updateRequest, variantId, toast, t]);

  return (
    <>
      <GridItem gridColumn="pa">
        {paFileIdentifier && (
          <Box w="fit-content" margin="auto">
            <ExpandImage variant="scale" identifier={paFileIdentifier}>
              <Img
                src={clientEndpoints.previewImage(paFileIdentifier, 312)}
                objectFit="cover"
                maxW="100%"
              />
            </ExpandImage>
          </Box>
        )}
      </GridItem>
      <GridItem
        gridColumn="mid"
        gridRow={1}
        as={Flex}
        alignItems="center"
        justifyContent="center"
      >
        {paFileId != null && variantPhotoId !== paFileId && (
          <Tooltip label={t('garp.copyValue')}>
            <IconButton
              isLoading={loading}
              isDisabled={loading}
              animation={fadeInScaleAnimation()}
              aria-label="copy"
              icon={<Icons.ArrowRight />}
              onClick={copyPaPhoto}
            />
          </Tooltip>
        )}
      </GridItem>
      <GridItem gridColumn="garp">
        <Photo
          variantId={variantId}
          allowEdit={true}
          folderId={folderId}
          onPhotoFetched={(fileId) => {
            setVariantPhotoId(fileId);
          }}
        />
      </GridItem>
    </>
  );
}

export function GarpWeight({
  rowIndex,
  compositions,
}: {
  rowIndex: number;
  compositions: Composition[];
}) {
  const { t } = useTranslation();
  const {
    register,
    registerAutoUpdate,
    triggerSubmit,
    formState,
    setValue,
    watch,
  } = useActiveContext(GarpFormContext);

  const watchedWeight = watch('weight');
  const totalWeight = useMemo(() => {
    return sum(compositions.flatMap((c) => c.materials.map((m) => m.weight)));
  }, [compositions]);

  return (
    <>
      <GridItem gridColumn="pa" as={Flex} flexDir="column" gap={flexGap}>
        <Text>{t('composition.weight')}</Text>
        <Weight compositions={compositions} />
      </GridItem>
      <GridItem
        gridColumn="mid"
        gridRow={rowIndex}
        as={Flex}
        alignItems="center"
        justifyContent="center"
      >
        {totalWeight !== watchedWeight && (
          <Tooltip label={t('garp.copyValue')}>
            <IconButton
              animation={fadeInScaleAnimation()}
              aria-label="copy"
              icon={<Icons.ArrowRight />}
              onClick={() => {
                setValue('weight', totalWeight);
                triggerSubmit();
              }}
            />
          </Tooltip>
        )}
      </GridItem>
      <GridItem gridColumn="garp">
        <AutoUpdateWrapper
          autoUpdateProps={registerAutoUpdate}
          path="weight"
          boxProps={{ flexGrow: '1' }}
        >
          <FormControl>
            <FormLabel>{t('composition.weight')}</FormLabel>
            <InputGroup>
              <Input
                {...register('weight', {
                  setValueAs: (v) => setValueAsDecimalExtender(v, true),
                  min: { value: 0, message: t('errors.notNegative') },
                  onBlur: triggerSubmit,
                })}
                variant="texas"
              />
              <InputRightAddon h="auto" border="none">
                {t('composition.kg')}
              </InputRightAddon>
            </InputGroup>
            <ErrorLabel text={formState.errors.weight?.message} />
          </FormControl>
        </AutoUpdateWrapper>
      </GridItem>
    </>
  );
}

export function GarpMaterial({
  rowIndex,
  compositions,
}: {
  rowIndex: number;
  compositions: Composition[];
}) {
  const { t } = useTranslation();
  const {
    control,
    registerAutoUpdate,
    triggerSubmit,
    watch,
    setValue,
    metadata: { materials, materialsLoading },
  } = useActiveContext(GarpFormContext);

  const watchedMaterial = watch('materialId');
  const setMaterialId = useMemo(() => {
    if (!materials) {
      return null;
    }

    const combinedMaterials = compositions.flatMap((c) =>
      c.materials.map((m) => m.material.id),
    );

    return extractCombinedMaterial(materials, combinedMaterials)?.id ?? null;
  }, [compositions, materials]);

  return (
    <>
      <GridItem gridColumn="pa" as={Flex} flexDir="column" gap={flexGap}>
        <Text>{t('general.materials')}</Text>
        <Materials
          compositions={compositions}
          metadataField="articleTextField"
        />
      </GridItem>
      <GridItem
        gridColumn="mid"
        gridRow={rowIndex}
        as={Flex}
        alignItems="center"
        justifyContent="center"
      >
        {setMaterialId !== watchedMaterial && setMaterialId && (
          <Tooltip label={t('garp.copyValue')}>
            <IconButton
              animation={fadeInScaleAnimation()}
              aria-label="copy"
              icon={<Icons.ArrowRight />}
              onClick={() => {
                setValue('materialId', setMaterialId);
                triggerSubmit();
              }}
            />
          </Tooltip>
        )}
      </GridItem>
      <GridItem gridColumn="garp">
        <AutoUpdateWrapper
          autoUpdateProps={registerAutoUpdate}
          path="materialId"
          boxProps={{ flexGrow: '1' }}
        >
          <FormControl>
            <FormLabel>{t('general.material')}</FormLabel>
            <Controller
              name="materialId"
              control={control}
              render={({ field }) => (
                <TexasSelect
                  onBlur={triggerSubmit}
                  onChange={(e) => field.onChange(e?.value ?? null)}
                  menuPlacement="auto"
                  isLoading={materialsLoading}
                  isClearable={true}
                  options={materials?.map((m) => ({
                    label: `${m.name} (${m.code})`,
                    value: m.id,
                  }))}
                  value={
                    field.value
                      ? {
                          value: field.value,
                          label: materials?.find((d) => d.id === field.value)
                            ?.name,
                        }
                      : undefined
                  }
                />
              )}
            />
          </FormControl>
        </AutoUpdateWrapper>
      </GridItem>
    </>
  );
}

export function GarpTechnique({
  compositions,
  rowIndex,
}: {
  compositions: Composition[];
  rowIndex: number;
}) {
  const { t } = useTranslation();
  const { control, registerAutoUpdate, triggerSubmit, watch, setValue } =
    useActiveContext(GarpFormContext);
  const { data, loading, refetch } = useApiResource(techniquesApi.getAll);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const watchedTechnique = watch('techniqueId');
  const setTechniqueId = useMemo(() => {
    if (!data) {
      return null;
    }

    const combinedIds = compositions.flatMap((c) =>
      c.materials.flatMap((m) => m.techniques.map((t) => t.id)),
    );

    const uniqueIds = combinedIds.filter(onlyUnique);

    if (uniqueIds.length > 1) {
      return null;
    }

    return uniqueIds.at(0);
  }, [compositions, data]);

  return (
    <>
      <GridItem gridColumn="pa" as={Flex} flexDir="column" gap={flexGap}>
        <Text>{t('configuration.techniques')}</Text>
        <Techniques compositions={compositions} />
      </GridItem>
      <GridItem
        gridColumn="mid"
        gridRow={rowIndex}
        as={Flex}
        alignItems="center"
        justifyContent="center"
      >
        {setTechniqueId !== watchedTechnique && setTechniqueId && (
          <Tooltip label={t('garp.copyValue')}>
            <IconButton
              animation={fadeInScaleAnimation()}
              aria-label="copy"
              icon={<Icons.ArrowRight />}
              onClick={() => {
                setValue('techniqueId', setTechniqueId);
                triggerSubmit();
              }}
            />
          </Tooltip>
        )}
      </GridItem>
      <GridItem gridColumn="garp">
        <AutoUpdateWrapper
          autoUpdateProps={registerAutoUpdate}
          path="techniqueId"
          boxProps={{ flexGrow: '1' }}
        >
          <FormControl>
            <FormLabel>{t('general.technique')}</FormLabel>
            <Controller
              name="techniqueId"
              control={control}
              render={({ field }) => (
                <TexasSelect
                  onBlur={triggerSubmit}
                  onChange={(e) => field.onChange(e?.value ?? null)}
                  menuPlacement="auto"
                  isLoading={loading}
                  isClearable={true}
                  options={data?.map((m) => ({ label: m.value, value: m.id }))}
                  value={
                    field.value
                      ? {
                          value: field.value,
                          label: data?.find((d) => d.id === field.value)?.value,
                        }
                      : undefined
                  }
                />
              )}
            />
          </FormControl>
        </AutoUpdateWrapper>
      </GridItem>
    </>
  );
}

export function GarpExtraDescription() {
  const { t } = useTranslation();

  return (
    <>
      <GridItem gridColumn="garp">
        <FormLabel>{t('garp.extraDescription')}</FormLabel>
        <GarpTextField textIndex={0} />
      </GridItem>
    </>
  );
}

export function GarpColour({ compositions }: { compositions: Composition[] }) {
  const { t } = useTranslation();

  return (
    <>
      <GridItem gridColumn="pa" as={Flex} flexDir="column" gap={flexGap}>
        <Text>{t('general.colors')}</Text>
        <Colors compositions={compositions} />
      </GridItem>
      <GridItem gridColumn="garp">
        <FormLabel>{t('garp.colour')}</FormLabel>
        <GarpTextField textIndex={1} />
      </GridItem>
    </>
  );
}

export function GarpTextMaterial({ rowIndex }: { rowIndex: number }) {
  const { t } = useTranslation();
  const {
    triggerSubmit,
    setValue,
    watch,
    metadata: { materials },
  } = useActiveContext(GarpFormContext);
  const watchedMaterial = watch('texts.2.text');
  const watchedMaterialId = watch('materialId');

  const setMaterialText = useMemo(() => {
    if (!materials) {
      return null;
    }

    return materials.find((x) => x.id == watchedMaterialId)?.articleTextField;
  }, [materials, watchedMaterialId]);

  return (
    <>
      <GridItem
        gridColumn="mid"
        gridRow={rowIndex}
        as={Flex}
        alignItems="center"
        justifyContent="center"
      >
        {setMaterialText !== watchedMaterial && setMaterialText && (
          <Tooltip label={t('garp.setGarpValue', { value: setMaterialText })}>
            <IconButton
              animation={fadeInScaleAnimation()}
              aria-label="copy"
              icon={<Icons.ArrowRightBottom />}
              onClick={() => {
                setValue('texts.2.text', setMaterialText);
                triggerSubmit();
              }}
            />
          </Tooltip>
        )}
      </GridItem>
      <GridItem gridColumn="garp">
        <FormLabel>{t('general.material')}</FormLabel>
        <GarpTextField textIndex={2} />
      </GridItem>
    </>
  );
}

export function GarpTextSize({
  compositions,
  rowIndex,
}: {
  compositions: Composition[];
  rowIndex: number;
}) {
  const { t } = useTranslation();
  const { triggerSubmit, setValue, watch } = useActiveContext(GarpFormContext);
  const watchedSize = watch('texts.3.text');
  const derivedSize = extractDimensionSize(compositions, 20);

  return (
    <>
      <GridItem gridColumn="pa" as={Flex} flexDir="column" gap={flexGap}>
        <Text>{t('configuration.dimensions')}</Text>
        <Dimensions compositions={compositions} />
      </GridItem>
      <GridItem
        gridColumn="mid"
        gridRow={rowIndex}
        as={Flex}
        alignItems="center"
        justifyContent="center"
      >
        {derivedSize && derivedSize !== watchedSize && (
          <Tooltip label={t('garp.copyValue')}>
            <IconButton
              animation={fadeInScaleAnimation()}
              aria-label="copy"
              icon={<Icons.ArrowRight />}
              onClick={() => {
                setValue('texts.3.text', derivedSize);
                triggerSubmit();
              }}
            />
          </Tooltip>
        )}
      </GridItem>
      <GridItem gridColumn="garp">
        <FormLabel>{t('general.size')}</FormLabel>
        <GarpTextField textIndex={3} />
      </GridItem>
    </>
  );
}

export function OtherCustomerInfoTextField({ index }: { index: number }) {
  const { t } = useTranslation();
  return (
    <>
      <GridItem gridColumn="garp">
        <FormLabel>{t('garp.otherCustomerInfo')}</FormLabel>
        <GarpTextField textIndex={index} />
      </GridItem>
    </>
  );
}

export function PaDocumentNoTextField({
  rowIndex,
  paNumber,
}: {
  rowIndex: number;
  paNumber?: string;
}) {
  const { t } = useTranslation();
  const { triggerSubmit, setValue, watch } = useActiveContext(GarpFormContext);
  const watchedPaNo = watch('texts.6.text');
  return (
    <>
      <GridItem gridColumn="pa" as={Flex} flexDir="column" gap={flexGap}>
        <Box>
          <MetadataList>
            <MetadataListRow label={t('productApproval.number')}>
              {paNumber}
            </MetadataListRow>
          </MetadataList>
        </Box>
      </GridItem>
      <GridItem
        gridColumn="mid"
        gridRow={rowIndex}
        as={Flex}
        alignItems="center"
        justifyContent="center"
      >
        {paNumber && paNumber !== watchedPaNo && (
          <Tooltip label={t('garp.copyValue')}>
            <IconButton
              animation={fadeInScaleAnimation()}
              aria-label="copy"
              icon={<Icons.ArrowRight />}
              onClick={() => {
                setValue('texts.6.text', paNumber);
                triggerSubmit();
              }}
            />
          </Tooltip>
        )}
      </GridItem>
      <GridItem gridColumn="garp">
        <FormLabel>{t('garp.paDocumentNo')}</FormLabel>
        <GarpTextField textIndex={6} />
      </GridItem>
    </>
  );
}

export function GarpCertificateMaterialText({
  rowIndex,
}: {
  rowIndex: number;
}) {
  const { t } = useTranslation();
  const {
    triggerSubmit,
    setValue,
    watch,
    metadata: { materials },
  } = useActiveContext(GarpFormContext);
  const watchedText5 = watch('texts.5.text');
  const watchedMaterialId = watch('materialId');

  const setMaterialText = useMemo(() => {
    if (!materials) {
      return null;
    }

    return materials.find((x) => x.id == watchedMaterialId)
      ?.certificationTextField;
  }, [materials, watchedMaterialId]);

  return (
    <>
      <GridItem
        gridColumn="mid"
        gridRow={rowIndex}
        as={Flex}
        alignItems="center"
        justifyContent="center"
      >
        {setMaterialText !== watchedText5 && setMaterialText && (
          <Tooltip label={t('garp.setGarpValue', { value: setMaterialText })}>
            <IconButton
              animation={fadeInScaleAnimation()}
              aria-label="copy"
              icon={<Icons.ArrowRightBottom />}
              onClick={() => {
                setValue('texts.5.text', setMaterialText);
                triggerSubmit();
              }}
            />
          </Tooltip>
        )}
      </GridItem>
      <GridItem gridColumn="garp">
        <FormLabel>{t('configuration.certificationTextField')}</FormLabel>
        <GarpTextField textIndex={5} />
      </GridItem>
    </>
  );
}

export function GarpSupplierItemCode({
  supplierItemCode,
  rowIndex,
}: {
  supplierItemCode?: string;
  rowIndex: number;
}) {
  const { t } = useTranslation();
  const { triggerSubmit, setValue, watch } = useActiveContext(GarpFormContext);
  const watchedSupplierItemCode = watch('texts.7.text');

  return (
    <>
      <GridItem gridColumn="pa" as={Flex} flexDir="column" gap={flexGap}>
        <Box>
          <MetadataList>
            <MetadataListRow label={t('productApproval.externalItemCode')}>
              {supplierItemCode && supplierItemCode.length > 0
                ? supplierItemCode
                : '-'}
            </MetadataListRow>
          </MetadataList>
        </Box>
      </GridItem>
      <GridItem
        gridColumn="mid"
        gridRow={rowIndex}
        as={Flex}
        alignItems="center"
        justifyContent="center"
      >
        {supplierItemCode &&
          supplierItemCode !== watchedSupplierItemCode &&
          supplierItemCode.length <= 20 && (
            <Tooltip label={t('garp.copyValue')}>
              <IconButton
                animation={fadeInScaleAnimation()}
                aria-label="copy"
                icon={<Icons.ArrowRight />}
                onClick={() => {
                  setValue('texts.7.text', supplierItemCode);
                  triggerSubmit();
                }}
              />
            </Tooltip>
          )}
      </GridItem>
      <GridItem gridColumn="garp">
        <FormLabel>{t('garp.supplierItemCode')}</FormLabel>
        <GarpTextField textIndex={7} />
      </GridItem>
    </>
  );
}

export function GarpSupplierColour({
  compositions,
}: {
  compositions: Composition[];
}) {
  const { t } = useTranslation();

  return (
    <>
      <GridItem gridColumn="pa" as={Flex} flexDir="column" gap={flexGap}>
        <Text>{t('general.colors')}</Text>
        <Colors compositions={compositions} />
      </GridItem>
      <GridItem gridColumn="garp">
        <FormLabel>{t('garp.supplierColorCode')}</FormLabel>
        <GarpTextField textIndex={8} />
      </GridItem>
    </>
  );
}

export function GarpSupplierTextMaterial({
  compositions,
}: {
  compositions: Composition[];
}) {
  const { t } = useTranslation();

  return (
    <>
      <GridItem gridColumn="pa" as={Flex} flexDir="column" gap={flexGap}>
        <Text>{t('general.materials')}</Text>
        <Materials
          compositions={compositions}
          metadataField="articleTextField"
        />
      </GridItem>
      <GridItem gridColumn="garp">
        <FormLabel>{t('garp.supplierMaterial')}</FormLabel>
        <GarpTextField textIndex={9} />
      </GridItem>
    </>
  );
}

export function GarpSupplierTextSize({
  compositions,
  rowIndex,
}: {
  compositions: Composition[];
  rowIndex: number;
}) {
  const { t } = useTranslation();
  const { triggerSubmit, setValue, watch } = useActiveContext(GarpFormContext);
  const watchedSize = watch('texts.10.text');
  const derivedSize = extractDimensionSize(compositions, 20);

  return (
    <>
      <GridItem gridColumn="pa" as={Flex} flexDir="column" gap={flexGap}>
        <Text>{t('configuration.dimensions')}</Text>
        <Dimensions compositions={compositions} />
      </GridItem>
      <GridItem
        gridColumn="mid"
        gridRow={rowIndex}
        as={Flex}
        alignItems="center"
        justifyContent="center"
      >
        {derivedSize && derivedSize !== watchedSize && (
          <Tooltip label={t('garp.copyValue')}>
            <IconButton
              animation={fadeInScaleAnimation()}
              aria-label="copy"
              icon={<Icons.ArrowRight />}
              onClick={() => {
                setValue('texts.10.text', derivedSize);
                triggerSubmit();
              }}
            />
          </Tooltip>
        )}
      </GridItem>
      <GridItem gridColumn="garp">
        <FormLabel>{t('garp.supplierSize')}</FormLabel>
        <GarpTextField textIndex={10} />
      </GridItem>
    </>
  );
}

export function OtherSupplierInfoTextFields() {
  const { t } = useTranslation();
  return (
    <>
      <GridItem gridColumn="garp">
        <FormLabel>{t('garp.otherSupplierInfo')}</FormLabel>
        <GarpTextField textIndex={11} />
        <FormLabel>{t('garp.otherSupplierInfo')}</FormLabel>
        <GarpTextField textIndex={12} />
        <FormLabel>{t('garp.otherSupplierInfo')}</FormLabel>
        <GarpTextField textIndex={13} />
        <FormLabel>{t('garp.wepProductLine')}</FormLabel>
        <GarpTextField textIndex={14} />
      </GridItem>
    </>
  );
}
